import { OEP_GATES, PhaseIndex, type IOEPSeasonTimeline, type OEPGateKey, type OEPPhase } from 'agu_oep_core';
import type { OEPGateSummaryView, OEPSendPreviewView, OEPTallyView } from '../../data/oep-actions.service';
import { DaysUntil, JoinAnd } from '../shared/format';
import { NumOrNull, Str, StrOrNull, type OEPRow } from '../shared/rows';

/**
 * Program status (AGU staff) — markup.html:843-905, logic.js:619-631 (opsActions) and :736-763
 * (staffStats, sectionRows, reminderLabel). Pure: every string and state the view shows is built here.
 */

/** OEP Program Stats (metadata/queries/oep/program-stats.sql), one row for the active season. */
export interface OEPProgramStatsRow {
    PresenterCount: number; DraftsUploaded: number; AssignmentCount: number; ReviewsSubmitted: number;
    NominationCount: number; SectionCount: number; SlatesSubmitted: number;
}

/** OEP Section Status (metadata/queries/oep/section-status.sql), one row per section. */
export interface OEPSectionStatusRow {
    SectionID: string; SectionName: string; PresenterCount: number; DraftsUploaded: number; AssignmentCount: number;
    ReviewsSubmitted: number; NominationCount: number; CommitteeMemberCount: number;
    SlateStatus: 'Draft' | 'Submitted' | null; SelectionID: string | null;
}

/** Pinned MJ Query IDs (metadata/queries/oep-*.json); MJ: Query Permissions grant both to OEP Staff only. */
export const OEP_PROGRAM_STATS_QUERY_ID = '0E900000-0000-4000-9000-000000000052';
export const OEP_SECTION_STATUS_QUERY_ID = '0E900000-0000-4000-9000-000000000054';

const n = (r: OEPRow, f: string): number => NumOrNull(r, f) ?? 0;

/** The OEP Program Stats row (none without an active season). */
export function ParseStatsRow(r: OEPRow | undefined): OEPProgramStatsRow | null {
    if (!r) return null;
    return {
        PresenterCount: n(r, 'PresenterCount'), DraftsUploaded: n(r, 'DraftsUploaded'), AssignmentCount: n(r, 'AssignmentCount'),
        ReviewsSubmitted: n(r, 'ReviewsSubmitted'), NominationCount: n(r, 'NominationCount'), SectionCount: n(r, 'SectionCount'),
        SlatesSubmitted: n(r, 'SlatesSubmitted'),
    };
}

/** One OEP Section Status row. */
export function ParseSectionRow(r: OEPRow): OEPSectionStatusRow {
    const slate = StrOrNull(r, 'SlateStatus');
    return {
        SectionID: Str(r, 'SectionID'), SectionName: Str(r, 'SectionName'), PresenterCount: n(r, 'PresenterCount'),
        DraftsUploaded: n(r, 'DraftsUploaded'), AssignmentCount: n(r, 'AssignmentCount'), ReviewsSubmitted: n(r, 'ReviewsSubmitted'),
        NominationCount: n(r, 'NominationCount'), CommitteeMemberCount: n(r, 'CommitteeMemberCount'),
        SlateStatus: slate === 'Draft' || slate === 'Submitted' ? slate : null, SelectionID: StrOrNull(r, 'SelectionID'),
    };
}

export interface OEPStatTile { Label: string; Value: string; Note: string }
export type OEPBadgeTone = 'ok' | 'warn' | 'hot' | 'neutral';
export interface OEPWinnersCell { Label: string; Tone: OEPBadgeTone }
export type OEPGateState = 'done' | 'next' | 'later';
export interface OEPGateRow { Key: OEPGateKey; When: string; Title: string; Note: string; Label: 'Done ✓' | 'Run'; State: OEPGateState; Enabled: boolean }
export interface OEPSectionRowVM { SectionID: string; Name: string; Drafts: string; Reviews: string; Nominations: string; Winners: OEPWinnersCell; ReopenID: string | null }
/** One CSV row: exactly the By section table's columns. */
export type OEPSectionCsvRow = { Section: string; Drafts: string; Reviews: string; Nominations: number; Winners: string };

/** The fixed empty line under "Needs a nudge" (task 29 brief). */
export const OEP_NO_NUDGE = 'Nothing needs a nudge right now.';
/** A draft-upload share under this is "behind" (task 29 brief). */
const DRAFT_BEHIND = 0.75;

const frac = (x: number, y: number): string => `${x} / ${y}`;

/** The four tiles (logic.js:736-741), from OEP Program Stats; no row (no season) reads as zeros. */
export function StatTiles(r: OEPProgramStatsRow | null): OEPStatTile[] {
    const s = r ?? { PresenterCount: 0, DraftsUploaded: 0, AssignmentCount: 0, ReviewsSubmitted: 0, NominationCount: 0, SectionCount: 0, SlatesSubmitted: 0 };
    return [
        { Label: 'Presenters', Value: frac(s.DraftsUploaded, s.PresenterCount), Note: 'drafts uploaded' },
        { Label: 'Reviewers', Value: frac(s.ReviewsSubmitted, s.AssignmentCount), Note: 'feedback submitted' },
        { Label: 'Nominations', Value: String(s.NominationCount), Note: `across ${s.SectionCount} sections` },
        { Label: 'Winners', Value: frac(s.SlatesSubmitted, s.SectionCount), Note: 'sections submitted' },
    ];
}

/**
 * One row per OEP_GATES entry (logic.js:619-631): a gate whose To phase is at or before the current phase
 * is "Done ✓"; the NextGate (From = current phase) runs; later gates show "Run", disabled.
 */
export function GateRows(phase: OEPPhase, timeline: IOEPSeasonTimeline): OEPGateRow[] {
    const at = PhaseIndex(phase);
    return OEP_GATES.map((g) => {
        const state: OEPGateState = PhaseIndex(g.To) <= at ? 'done' : g.From === phase ? 'next' : 'later';
        return {
            Key: g.Key, When: timeline.Dates[g.DateKey].Label, Title: g.Title, Note: g.Note,
            Label: state === 'done' ? 'Done ✓' : 'Run', State: state, Enabled: state === 'next',
        };
    });
}

const emails = (count: number): string => `${count} ${count === 1 ? 'email' : 'emails'}`;

/**
 * The confirm message for a send: one sentence per email in the purpose, from the server's PreviewSend
 * result (mode and redirect address come from the server, never from the UI). Empty → "No emails will be sent."
 */
export function PreviewLine(preview: OEPSendPreviewView): string {
    if (preview.Previews.length === 0) return 'No emails will be sent.';
    return preview.Previews.map((p) => {
        const what = emails(p.Recipients);
        switch (p.Mode) {
            case 'redirect': return `Redirect mode: ${what} will go to ${p.RedirectedTo ?? 'the redirect address'}, not to participants.`;
            case 'dryrun': return `Dry run: ${what} will be built but not sent.`;
            case 'live': return `Live: ${what} will go to participants.`;
            case 'sandbox': return `Sandbox: ${what} will be validated by SendGrid and delivered to ${p.RedirectedTo ?? 'the redirect address'}.`;
        }
    }).join(' ');
}

/** Any preview in live mode → the confirm is a warning (real participants receive it). */
export function IsLive(preview: OEPSendPreviewView): boolean {
    return preview.Previews.some((p) => p.Mode === 'live');
}

/** "{Redirected + Sent + DryRun} sent ({Mode}), {Failed} failed" (task 29 brief). */
export function TallyLine(t: OEPTallyView): string {
    return `${t.Redirected + t.Sent + t.DryRun} sent (${t.Mode}), ${t.Failed} failed`;
}

/** The failures of a set of tallies, "Failed: a; b", or '' when none failed. */
export function FailureLine(tallies: readonly OEPTallyView[]): string {
    const errors = tallies.flatMap((t) => t.Errors);
    return errors.length === 0 ? '' : `Failed: ${errors.join('; ')}`;
}

/** After RunGate: "{FromPhase} → {ToPhase}", a line per mail tally, then the failures. */
export function GateToast(s: OEPGateSummaryView): string {
    const failures = FailureLine(s.Mail);
    return [`${s.FromPhase} → ${s.ToPhase}`, ...s.Mail.map(TallyLine), ...(failures ? [failures] : [])].join('. ');
}

/** Winners column (logic.js:106-113 labels; "No presentations" is the spec §5 mismatch row). */
export function WinnersCell(r: OEPSectionStatusRow): OEPWinnersCell {
    if (r.SlateStatus === 'Submitted') return { Label: 'Submitted', Tone: 'ok' };
    if (r.SlateStatus === 'Draft') return { Label: 'In progress', Tone: 'warn' };
    if (r.PresenterCount === 0) return { Label: 'No presentations', Tone: 'neutral' };
    if (r.CommitteeMemberCount === 0) return { Label: 'No committee', Tone: 'hot' };
    return { Label: 'Not started', Tone: 'hot' };
}

/** The By section rows; Reopen is offered on a Submitted slate only while committees are Selecting. */
export function SectionRows(rows: readonly OEPSectionStatusRow[], phase: OEPPhase): OEPSectionRowVM[] {
    return rows.map((r) => ({
        SectionID: r.SectionID, Name: r.SectionName,
        Drafts: frac(r.DraftsUploaded, r.PresenterCount), Reviews: frac(r.ReviewsSubmitted, r.AssignmentCount),
        Nominations: String(r.NominationCount), Winners: WinnersCell(r),
        ReopenID: phase === 'Selecting' && r.SlateStatus === 'Submitted' && r.SelectionID !== null ? r.SelectionID : null,
    }));
}

/** The By section table as CSV rows (neutralized by the caller before ExportEngine.toCSV). */
export function SectionCsvRows(rows: readonly OEPSectionStatusRow[]): OEPSectionCsvRow[] {
    return rows.map((r) => ({
        Section: r.SectionName, Drafts: frac(r.DraftsUploaded, r.PresenterCount), Reviews: frac(r.ReviewsSubmitted, r.AssignmentCount),
        Nominations: r.NominationCount, Winners: WinnersCell(r).Label,
    }));
}

/** CSV columns, in table order. */
export const OEP_SECTION_CSV_COLUMNS: readonly (keyof OEPSectionCsvRow)[] = ['Section', 'Drafts', 'Reviews', 'Nominations', 'Winners'];

/**
 * "Needs a nudge", computed from the section rows (the prototype's list, markup.html:900-903, is static):
 * Selecting → sections with presentations and a committee but no submitted slate; any phase → sections
 * with no committee; DraftUpload → sections under 75% of drafts uploaded.
 */
export function Nudges(phase: OEPPhase, rows: readonly OEPSectionStatusRow[], timeline: IOEPSeasonTimeline, now: Date): string[] {
    const out: string[] = [];
    if (phase === 'Selecting') {
        // Narrower than "no submitted slate" on purpose: a section with no presentations has nothing to pick, and a
        // section with no committee is already listed by the no-committee sentence below (no double listing).
        const late = rows.filter((r) => r.SlateStatus !== 'Submitted' && r.PresenterCount > 0 && r.CommitteeMemberCount > 0).map((r) => r.SectionName);
        if (late.length > 0) {
            const d = DaysUntil(timeline.Dates.SelectionDue.On, now);
            out.push(`${JoinAnd(late)} ${late.length === 1 ? 'hasn\'t' : 'haven\'t'} submitted winners, committee deadline is in ${d} ${d === 1 ? 'day' : 'days'}.`);
        }
    }
    if (phase === 'DraftUpload') {
        const behind = rows.filter((r) => r.PresenterCount > 0 && r.DraftsUploaded / r.PresenterCount < DRAFT_BEHIND).map((r) => r.SectionName);
        if (behind.length > 0) out.push(`${JoinAnd(behind)} ${behind.length === 1 ? 'is' : 'are'} behind on draft uploads.`);
    }
    const noCommittee = rows.filter((r) => r.CommitteeMemberCount === 0).map((r) => r.SectionName);
    if (noCommittee.length > 0) out.push(`${JoinAnd(noCommittee)} ${noCommittee.length === 1 ? 'has' : 'have'} no OEP section committee named yet.`);
    return out;
}

/** Reminders apply only in the four phases the server's ReminderSend handles (apps/MJAPI/src/oep/gates.ts). */
export function RemindersEnabled(phase: OEPPhase): boolean {
    return phase === 'DraftUpload' || phase === 'Reviewing' || phase === 'Nominating' || phase === 'Selecting';
}
